import { Component, Inject, Input, OnInit } from '@angular/core';
import { Router, ResolveEnd } from '@angular/router';

import { AuthService } from '@shared/modules/auth/services/auth.service';
import { OrganizationDataService } from '@shared/services/organization-data.service';
import { OrganizationService } from '@shared/services/organization.service';
import { TelemetryService } from '@shared/services/telemetry.service';
import { WINDOW } from '@shared/services/window.service';

import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'environments/environment';

// Icons
import { faBars, faExclamationTriangle, faMapMarkerAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'cr-header-menu',
  templateUrl: './header-menu.component.html',
  styleUrls: ['./header-menu.component.scss'],
  standalone: false
})
export class HeaderMenuComponent implements OnInit {

  @Input() activeTab: string;

  readonly CWK_ORGANIZATION_ID = this.organizationService.CWK_ORGANIZATION_ID;

  envPrefix = environment.urlAppHomePrefix ? ('/' + environment.urlAppHomePrefix) : '';
  isMobileMenuOpened = false;
  organization: any = null;
  urlRoot = environment.urlRoot;

  // Icons
  faBars = faBars;
  faExclamationTriangle = faExclamationTriangle;
  faMapMarkerAlt = faMapMarkerAlt;
  faSpinner = faSpinner;

  // Progress
  errorReadingOrganization = '';
  isReadingOrganization = false;

  constructor(
    private authService: AuthService,
    private organizationDataService: OrganizationDataService,
    private organizationService: OrganizationService,
    private router: Router,
    private telemetryService: TelemetryService,
    @Inject(WINDOW) private window: Window | null
  ) {

    // Set telemetry once the router has resolved the final route
    this.router.events.subscribe((routerData) => {
      if (routerData instanceof ResolveEnd) {
        this.setTelemetry(routerData.url);
      }
    });
  }

  ngOnInit(): void {

    // If there is no organization set, default to CwK
    if (!this.organizationService.getOrganization()) {
      this.organizationService.setOrganization(this.CWK_ORGANIZATION_ID);
    }

    // Watch organization for changes
    this.organizationService.getOrganizationChanged()
      .pipe(
        debounceTime(200),
        distinctUntilChanged()
      )
      .subscribe(organizationId => {

        // Read the organization. Default to CwK if organization was not found
        this.readOrganization(organizationId);
      });
  }

  goHome() {
    if (this.window) {
      const envPrefix = environment.urlAppHomePrefix ? (environment.urlAppHomePrefix + '/') : '';
      this.window.location.href = (this.organization.Code === 'cr' ? '/' : ('/' + envPrefix + this.organization.Code))/* + this.window.location.search*/;
    }
  }

  goUrl(url: string) {
    if (this.window) {
      this.window.location.href = environment.urlRoot + url/* + this.window.location.search*/;
    }
  }

  isInternal(): boolean {
    return this.authService.isAuthorized(['ADMIN', 'ORGADMIN']);
  }

  isSignedIn(): boolean {
    return this.authService.isAuthorized(['USER']);
  }

  isSignedInStudent(): boolean {
    return this.authService.isAuthorized(['STUDENT']);
  }

  signOut() {

    // Sign out - navigate to sign-out URL
    this.authService.signOut()
      .subscribe(() => {
        if (this.window) {
          this.window.location.href = environment.urlSignOut/* + this.window.location.search*/;
        }
      });
  }

  switchMobileMenu() {
    this.isMobileMenuOpened = !this.isMobileMenuOpened;
  }

  private readOrganization(organizationId: number): void {
    this.errorReadingOrganization = '';
    this.organization = null;
    this.isReadingOrganization = true;

    // Read organization
    this.organizationDataService.read(organizationId)
      .subscribe(
        (value) => {
          this.organization = value;

          // Set the home link for the organization
          this.organization.HomeLink = this.organization.Code === 'cr' ?
            '/' : (this.envPrefix + '/region/' + this.organization.URLIdentifier);

          this.isReadingOrganization = false;
        },
        (error) => {
          this.errorReadingOrganization = error;
          this.isReadingOrganization = false;
        }
      );
  }

  private setTelemetry(sourcePath: string): void {
    this.telemetryService.inject('CLICK', 'home-menu-item', sourcePath, true);
  }
}
